import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, BarChart3, Activity, Zap, LineChart as LineChartIcon, Wifi, WifiOff } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, ComposedChart, Bar } from "recharts";

const INITIAL_COMMODITIES = [
  { id: "gold", name: "Gold Futures", price: 2387.56, change: 2.34, contract: "May 2024" },
  { id: "cocoa", name: "Cocoa Futures", price: 3974.00, change: 2.36, contract: "May 2024" },
  { id: "crude_oil", name: "Crude Oil Futures", price: 81.20, change: -0.95, contract: "Jun 2024" },
  { id: "copper", name: "Copper Futures", price: 4.62, change: 1.34, contract: "May 2024" },
  { id: "coffee", name: "Coffee Futures", price: 2994.00, change: -1.24, contract: "Jul 2024" },
  { id: "wheat", name: "Wheat Futures", price: 568.25, change: 1.15, contract: "May 2024" },
];

const SIGNALS = [
  { name: "Price Momentum", value: "Strong Bullish", color: "#00ff41" },
  { name: "Trading Flow", value: "Significant Institutional", color: "#0ea5e9" },
  { name: "Sentiment Analysis", value: "Positive", color: "#00ff41" },
  { name: "Liquidity Depth", value: "High Volume", color: "#a855f7" },
];

const generateChartData = (base, days = 30) => {
  let price = base;
  return Array.from({ length: days }, (_, i) => {
    const change = (Math.random() - 0.48) * base * 0.02;
    price += change;
    return {
      day: i + 1,
      price: price,
      volume: Math.floor(Math.random() * 50000 + 10000),
      open: price - Math.random() * base * 0.01,
      high: price + Math.random() * base * 0.015,
      low: price - Math.random() * base * 0.015,
    };
  });
};

export default function PriceSignalsPage() {
  const [commodities, setCommodities] = useState(INITIAL_COMMODITIES);
  const [selectedCommodity, setSelectedCommodity] = useState(INITIAL_COMMODITIES[0]);
  const [timeframe, setTimeframe] = useState("1M");
  const [wsConnected, setWsConnected] = useState(false);
  const [flashingPrices, setFlashingPrices] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/prices`);
    
    ws.onopen = () => {
      console.log("Price stream connected");
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "init") {
        const serverPrices = data.prices;
        setCommodities(prev => prev.map(c => ({
          ...c,
          price: serverPrices[c.id]?.price ?? c.price,
          change: serverPrices[c.id]?.change ?? c.change,
        })));
        setLastUpdate(new Date());
      } 
      else if (data.type === "tick") {
        const updates = data.updates;
        const flashIds = {};
        
        setCommodities(prev => prev.map(c => {
          const update = updates.find(u => u.id === c.id);
          if (update) {
            flashIds[c.id] = update.tick >= 0 ? "up" : "down";
            return { ...c, price: update.price, change: update.change };
          }
          return c;
        }));
        
        // Update selected commodity
        const selectedUpdate = updates.find(u => u.id === selectedCommodity.id);
        if (selectedUpdate) {
          setSelectedCommodity(prev => ({
            ...prev,
            price: selectedUpdate.price,
            change: selectedUpdate.change
          }));
        }
        
        setFlashingPrices(flashIds);
        setTimeout(() => setFlashingPrices({}), 300);
        setLastUpdate(new Date());
      }
    };
    
    ws.onclose = () => {
      console.log("Price stream disconnected");
      setWsConnected(false);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, [selectedCommodity.id]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  const chartData = generateChartData(selectedCommodity.price);
  const latestPrice = selectedCommodity.price;
  const priceChange = selectedCommodity.change;

  return (
    <div data-testid="price-signals-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Price Signals
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            AI-powered price analysis and trading signals
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* WebSocket Status */}
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <>
                <Wifi className="w-4 h-4 text-[#00ff41]" />
                <span className="text-[10px] text-[#00ff41] font-mono uppercase">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-[#ff3b30]" />
                <span className="text-[10px] text-[#ff3b30] font-mono uppercase">Offline</span>
              </>
            )}
          </div>
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-24 h-8 rounded-none border-zinc-800 bg-black text-xs font-mono">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
              <SelectItem value="1D" className="text-xs font-mono">1 Day</SelectItem>
              <SelectItem value="1W" className="text-xs font-mono">1 Week</SelectItem>
              <SelectItem value="1M" className="text-xs font-mono">1 Month</SelectItem>
              <SelectItem value="3M" className="text-xs font-mono">3 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Commodity Selector */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {commodities.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCommodity(c)}
                className={`p-3 text-left border transition-all duration-100 ${
                  selectedCommodity.id === c.id
                    ? "bg-zinc-900 border-[#00ff41]"
                    : "bg-zinc-950/30 border-zinc-800 hover:border-zinc-600"
                } ${
                  flashingPrices[c.id] === "up" ? "!border-[#00ff41] !bg-[#00ff41]/10" :
                  flashingPrices[c.id] === "down" ? "!border-[#ff3b30] !bg-[#ff3b30]/10" : ""
                }`}
              >
                <p className="text-[10px] text-zinc-500 uppercase font-mono">{c.name}</p>
                <p className={`text-lg font-mono font-bold transition-colors duration-300 ${
                  flashingPrices[c.id] === "up" ? "text-[#00ff41]" :
                  flashingPrices[c.id] === "down" ? "text-[#ff3b30]" : "text-zinc-100"
                }`}>
                  ${c.price.toLocaleString()}
                </p>
                <p className={`text-[10px] font-mono ${c.change >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
                  {c.change >= 0 ? "+" : ""}{c.change.toFixed(2)}%
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Chart */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
                  {selectedCommodity.name}
                </CardTitle>
                <p className="text-[10px] text-zinc-500 font-mono">{selectedCommodity.contract}</p>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-mono font-bold transition-colors duration-300 ${
                  flashingPrices[selectedCommodity.id] === "up" ? "text-[#00ff41]" :
                  flashingPrices[selectedCommodity.id] === "down" ? "text-[#ff3b30]" : "text-zinc-100"
                }`}>
                  ${latestPrice.toFixed(2)}
                </p>
                <p className={`text-sm font-mono ${priceChange >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
                  {priceChange >= 0 ? "+" : ""}{priceChange.toFixed(2)}%
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="signalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ff41" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#00ff41" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis 
                  yAxisId="price"
                  tick={{ fill: "#71717a", fontSize: 10 }} 
                  axisLine={false} 
                  tickLine={false}
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => `$${v.toFixed(0)}`}
                />
                <YAxis 
                  yAxisId="volume"
                  orientation="right"
                  tick={{ fill: "#71717a", fontSize: 10 }} 
                  axisLine={false} 
                  tickLine={false}
                  tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
                />
                <RechartsTooltip 
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, fontSize: 11, fontFamily: "JetBrains Mono" }} 
                />
                <Bar yAxisId="volume" dataKey="volume" fill="#27272a" opacity={0.5} />
                <Area yAxisId="price" type="monotone" dataKey="price" stroke="#00ff41" fill="url(#signalGrad)" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Price Stats */}
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-zinc-800">
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Bid</p>
                <p className="text-sm font-mono text-zinc-300">${(latestPrice - 0.5).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Ask</p>
                <p className="text-sm font-mono text-zinc-300">${(latestPrice + 0.5).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Spread</p>
                <p className="text-sm font-mono text-zinc-300">$0.50</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-mono">Volume</p>
                <p className="text-sm font-mono text-zinc-300">54,230</p>
              </div>
            </div>

            {/* Last Update */}
            {lastUpdate && (
              <div className="text-center mt-2">
                <p className="text-[9px] text-zinc-600 font-mono">
                  Last tick: {lastUpdate.toLocaleTimeString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signals Panel */}
        <div className="space-y-4">
          {/* Summary Insights */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-[#eab308]" />
                Summary Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {SIGNALS.map((signal, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-900">
                  <div className="flex items-center gap-2">
                    <Activity className="w-3 h-3" style={{ color: signal.color }} />
                    <span className="text-xs text-zinc-400 font-mono">{signal.name}</span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className="rounded-none text-[9px] font-mono"
                    style={{ borderColor: signal.color, color: signal.color }}
                  >
                    {signal.value}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* AI Recommendations */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <LineChartIcon className="w-3.5 h-3.5 text-[#0ea5e9]" />
                AI Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="p-3 bg-[#00ff41]/5 border border-[#00ff41]/20">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#00ff41]" />
                  <span className="text-xs font-mono text-[#00ff41] uppercase">Bullish Signal</span>
                </div>
                <p className="text-[11px] text-zinc-400 font-mono">
                  Strong upward momentum detected. Consider long positions with target at ${(latestPrice * 1.05).toFixed(2)}.
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-zinc-500">Entry Point</span>
                  <span className="text-zinc-300">${latestPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-zinc-500">Take Profit</span>
                  <span className="text-[#00ff41]">${(latestPrice * 1.05).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-zinc-500">Stop Loss</span>
                  <span className="text-[#ff3b30]">${(latestPrice * 0.97).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-zinc-500">Risk/Reward</span>
                  <span className="text-[#0ea5e9]">1:1.67</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
